import { useMutation, useQuery } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { Editor } from "@tiptap/core";
import { NodeViewWrapper } from "@tiptap/react";
import a from "indefinite";
import { defer } from "lodash";
import { createRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ArtifactType,
  CreatedArtifactFragmentFragment,
  GetWysiwygArtifactQueryQuery,
  GetWysiwygArtifactQueryQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import { getRecognitionRecipientTitle } from "@apps/artifact/artifact-helpers";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import AppError from "@components/error/error";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import useMountedState from "@components/use-mounted-state/use-mounted-state";
import { batchClient } from "@graphql/client";
import { classNames } from "@helpers/css";
import { errorMatches, getUrl, toWithBackground } from "@helpers/helpers";

import ActionItemQuickCreate from "./action-item-quick-create";
import {
  artifactEmbedCardClassName,
  artifactEmbedTitleClassName,
} from "./artifact-embed-card";
import ArtifactError from "./artifact-error";
import ArtifactIcon from "./artifact-icon";
import ArtifactInfos from "./artifact-infos";
import ArtifactToolbar from "./artifact-toolbar";
import ArtifactComponentGoalKeyResults from "./goals/key-results";
import associateArtifactWithTopicOrArtifactMutation from "./graphql/associate-artifact-with-topic-mutation";
import getArtifactQuery from "./graphql/get-wysiwyg-artifact-query";
import { deleteArtifact } from "./helpers";

/**
 * We need a useQuery for 2 reasons:
 * - To fetch initially the artifact using the extension attr id
 * - Then to keep the artifact data up to date when its cache is updated in another view
 *
 * Then we use useMutation to create or update the artifact. We define a no-cache policy and ignore api response
 * to prevent the cache to be updated after the api call succeed, instead we update the cache optimistically before the api call.
 * We prevent async update of the artifact which would provide a broken UX while user types.
 *
 */
const ArtifactComponent = ({
  node,
  updateAttributes,
  selected,
  editor,
  getPos,
  extension,
  deleteNode,
}: {
  node: any;
  updateAttributes: (attributes: any) => void;
  selected: boolean;
  editor: Editor;
  getPos: () => number | undefined;
  extension: any;
  deleteNode: () => void;
}) => {
  const isMounted = useMountedState();
  const ref = createRef<HTMLDivElement | undefined>();
  const currentUser = currentUserVar();
  const location = useLocation<TFLocationState>();
  const {
    id,
    createdByUser,
    // copyId is used when pasting/dropping an artifact link in the notes
    // this is to delay thing while we associated the artifact with topic on the backend
    // once associated we can set the `id` and clear `copyId`.
    // if we don't do that, the artifact will be rendered before being associated with topic
    // which might lead to the artifact not being readable by meeting participants.
    copyId,
    quickCreate,
  } = node.attrs;
  const artifactTypeName = node.type.name;
  const isActionItem = artifactTypeName === ArtifactType.ActionItem;
  const isFeedback = artifactTypeName === ArtifactType.Feedback;
  const isRecognition = artifactTypeName === ArtifactType.Recognition;
  const l = useLabel();
  const isCreatedByCurrentUser = createdByUser?.id === currentUser.id;
  const artifactId = id || copyId;
  const isBeingCopied = !id && copyId;
  const isQuickCreating =
    isActionItem && !!quickCreate && !artifactId && isCreatedByCurrentUser;
  const [showCreateArtifactDialog, setShowCreateArtifactDialog] = useState(
    isCreatedByCurrentUser && !artifactId && !isQuickCreating,
  );

  // HOOKS
  // skip if the artifact has not been attributed an id (not existing yet)
  // skip when another participant to copying an artifact until it is associated topic topic/artifact
  const isBeingCopiedByAnotherUser = isBeingCopied && !isCreatedByCurrentUser;
  const skip = !artifactId || isBeingCopiedByAnotherUser;

  const { data, loading, error } = useQuery<
    GetWysiwygArtifactQueryQuery,
    GetWysiwygArtifactQueryQueryVariables
  >(getArtifactQuery, {
    fetchPolicy: "cache-and-network",
    variables: { artifactId: Number(artifactId) },
    skip,
    // The component renders every error state inline (missing, no-permission,
    // unexpected), so the shared error link's global toast is redundant here.
    // The shared link owns Sentry reporting, including unexpected failures.
    context: { skipGraphqlErrorHandling: true },
    client: batchClient,
  });
  const artifact = data?.artifact;
  const artifactType = data?.artifact?.artifactType || node.type.name;

  const seemsDeleted = id && data && data.artifact === null;
  const [associateArtifactWithTopicOrArtifact] = useMutation(
    associateArtifactWithTopicOrArtifactMutation,
  );

  // if existing artifact was added to notes, we need to associate it with the topic
  // so other participants can see the artifact being loaded when they have read permissions
  useEffect(() => {
    if (!copyId || id) {
      return;
    }
    if (!isCreatedByCurrentUser) {
      // only current user can associate an artifact
      return;
    }

    if (extension.options.topicId || extension.options.relatedArtifactId) {
      associateArtifactWithTopicOrArtifact({
        variables: {
          artifactId: copyId,
          topicId:
            extension.options.topicId || extension.options.relatedArtifactId,
        },

        onCompleted: () => {
          if (isMounted()) {
            updateAttributes({ copyId: null, id: copyId });
          }
        },
      });
    } else {
      setTimeout(() => {
        if (isMounted()) {
          updateAttributes({ copyId: null, id: copyId });
        }
      }, 6000); // 1s + debounce time of tiptap backend sync https://github.com/Topicflow/topicflow/blob/68e94db6bc205171d6345ee240fcd51d6104e6c1/websocket/server.js#L92
    }
  }, [id, copyId]);

  // HANDLERS
  const handleDelete = (callback?: () => void) => {
    deleteArtifact({ artifact, deleteNode, callback });
  };

  const handleCloseCreateArtifactDialog = (
    createdArtifact?: CreatedArtifactFragmentFragment,
  ) => {
    if (!createdArtifact) {
      deleteNode();
    } else if (createdArtifact.artifactType !== artifactType) {
      // in case user created another type of artifact in the dialog
      // we remove previous one and add the one that was created in dialog
      // Read the position before the node is removed: after that, Tiptap 3
      // returns `undefined`.
      const pos = getPos();
      deleteNode();
      editor
        .chain()
        .focus(pos)
        .insertContent({
          type: createdArtifact.artifactType,
          attrs: { uuid: node.attrs.uuid, id: createdArtifact.id },
        })
        .run();
    } else {
      updateAttributes({ id: createdArtifact.id });
      setShowCreateArtifactDialog(false);
    }
  };

  // update uuid attributes if there is one in database
  useEffect(() => {
    if (id && artifact?.uuid && artifact.uuid !== node.attrs.uuid) {
      defer(() => {
        if (isMounted()) {
          updateAttributes({ uuid: artifact.uuid });
        }
      });
    }
  }, [artifact, id, node.attrs.uuid]);

  // Make sure we close any create dialog if id is set
  useEffect(() => {
    if (artifactId) {
      setShowCreateArtifactDialog(false);
    }
  }, [artifactId]);

  // Browsers tend to bug because of the draggable attribute on the parent DOM element of this component
  // It seems that contentEditable=true does not play well with draggable=true.
  // https://github.com/ueberdosis/tiptap/issues/1668#issuecomment-997755214
  // https://discuss.prosemirror.net/t/nested-draggable-editors-behave-differently-in-all-browsers/3807/13
  useEffect(() => {
    const parentNode = ref.current?.parentNode as HTMLElement;
    if (parentNode) {
      parentNode.setAttribute("draggable", "false");
    }
  }, [ref]);

  const handleSavedComment = (comment?: { id: number }) => {
    updateAttributes({ commentId: comment?.id || null });
  };

  const sidebarTo = artifact
    ? toWithBackground({
        pathname: getUrl({
          artifactId: artifact.id,
          artifactType: artifactTypeName,
          meetingGroupId: extension.options.meetingGroupId,
          meetingId: extension.options.meetingId,
        }),
        location,
      })
    : "";

  return (
    <NodeViewWrapper
      className={classNames(
        "relative",
        // Shared embed-card chrome (also used by the goal page's alignment
        // section). `group` is on the wrapper so the floating toolbar's hover
        // bridge covers the full padding area; my-3 spaces embeds in notes.
        artifactEmbedCardClassName,
        "my-3 group",
        selected &&
          editor?.options?.editable &&
          "ring-2 ring-blue-200 ring-offset-2 extension-is-selected",
      )}
      ref={ref}
    >
      {showCreateArtifactDialog && (
        <ArtifactCreationDialog
          formOptions={{
            artifactType: artifactTypeName,
            meetingId: extension.options.meetingId,
            title: node.attrs.title || "",
          }}
          onClose={handleCloseCreateArtifactDialog}
        />
      )}
      {isQuickCreating ? (
        <ActionItemQuickCreate
          node={node}
          extension={extension}
          updateAttributes={updateAttributes}
          deleteNode={deleteNode}
          editor={editor}
          getPos={getPos}
        />
      ) : !artifact && loading ? (
        <div className="text-gray-500 text-sm py-1 italic flex items-center gap-2">
          <Loading mini size="5" className="shrink-0" />
          Loading {l(artifactTypeName)}
        </div>
      ) : isBeingCopiedByAnotherUser ? (
        <div className="text-gray-500 text-sm py-0.5 italic flex items-center gap-2">
          <Loading mini size="4" className="shrink-0" />
          <span className="flex-1">
            <span className="font-medium text-gray-600">
              {createdByUser.name}
            </span>{" "}
            is adding {a(l(artifactTypeName))}
          </span>
        </div>
      ) : !id && !isCreatedByCurrentUser ? (
        <div className="text-gray-500 text-sm py-0.5 italic flex items-center gap-2">
          <Loading mini size="4" className="shrink-0" />
          <span className="flex-1">
            <span className="font-medium text-gray-600">
              {createdByUser.name}
            </span>{" "}
            is creating {a(l(artifactTypeName))}
          </span>
        </div>
      ) : error &&
        errorMatches(
          error,
          "You do not have permission to access the instance with id",
        ) ? ( // USER DOES NOT HAVE PERMISSIONS TO VIEW ARTIFACT
        <ArtifactError onDeleteNode={handleDelete}>
          You don't have the permission to view this {l(artifactTypeName)}.
        </ArtifactError>
      ) : (error && errorMatches(error, "Invalid id")) || seemsDeleted ? ( // ARTIFACT HAS BEEN DELETED OR NO ACCESS
        <ArtifactError onDeleteNode={handleDelete}>
          This {l(artifactTypeName)} does not exist anymore.
        </ArtifactError>
      ) : error ? ( // UNKNOWN ERROR
        <ArtifactError onDeleteNode={handleDelete}>
          An unexpected error occurred. This {l(artifactTypeName)} can not be
          displayed.
        </ArtifactError>
      ) : artifact && artifactTypeName !== artifact?.artifactType ? ( // When artifact is added as wrong type
        <ArtifactError onDeleteNode={handleDelete}>
          <>
            We cannot display this {a(l(artifactTypeName))} because it is{" "}
            <a
              href={getUrl({
                artifactId: artifact.id,
                artifactType: artifact.artifactType,
              })}
              target="_blank"
              rel="noreferrer"
            >
              {a(l(artifact.artifactType))}
            </a>
            .
          </>
        </ArtifactError>
      ) : artifact ? (
        // ARTIFACT EXIST
        <div
          className={classNames(
            "flex-1",
            // Do not remove min-w-0, as it prevents long text/url to wrap correctly
            // https://imgur.com/a/S90aplB
            "min-w-0",
          )}
        >
          <div
            className={classNames(
              "flex flex-1 items-start min-w-0 gap-2",
              "@container/wysiwyg-artifact",
              // `group` is now on the outer NodeViewWrapper so the toolbar's
              // group-hover bridge covers the wrapper's full padding area.
              "not-prose", // do not apply tailwind typography to the component
            )}
          >
            <ArtifactToolbar
              syncedArtifact={artifact}
              onRemoveFromNotes={handleDelete}
              extension={extension}
              onSavedComment={handleSavedComment}
            />
            <ArtifactIcon node={node} syncedArtifact={artifact} />
            {isRecognition || isFeedback ? (
              <div className="flex flex-1 flex-col min-w-0">
                {/* Only the header is a link — sub-title (recognition message)
                    stays plain text since the same link would just duplicate
                    the header's destination. */}
                <AppLink
                  to={sidebarTo}
                  className="text-sm font-medium text-gray-800 hover:underline"
                >
                  {artifact?.__typename === "FeedbackArtifactNode"
                    ? artifact.title
                    : getRecognitionRecipientTitle(artifact)}
                </AppLink>
                {artifact?.__typename === "RecognitionArtifactNode" && (
                  <span className="text-xs font-normal text-gray-500">
                    {artifact.title}
                  </span>
                )}
              </div>
            ) : id ? (
              <AppLink to={sidebarTo} className={artifactEmbedTitleClassName}>
                {artifact?.__typename === "DecisionArtifactNode"
                  ? artifact?.decision
                  : artifact?.title}
              </AppLink>
            ) : (
              <span className="flex flex-1 items-center min-w-0 text-sm font-medium text-gray-800 hover:underline gap-2">
                Creating {l(artifactTypeName)}
              </span>
            )}
            <ArtifactInfos
              syncedArtifact={artifact}
              loading={loading}
              onDelete={handleDelete}
              onSavedComment={handleSavedComment}
            />
          </div>
          {artifact.__typename === "GoalArtifactNode" && (
            <ArtifactComponentGoalKeyResults artifactId={artifact.id} />
          )}
          {/* Description editor and inline comments are intentionally
              hidden in the embed — that content lives in the artifact
              sidebar instead. */}
        </div>
      ) : null}
    </NodeViewWrapper>
  );
};

const ArtifactComponentWithSentry = withErrorBoundary(ArtifactComponent, {
  fallback: (
    <NodeViewWrapper>
      <AppError description={"This artifact could not be displayed."} />
    </NodeViewWrapper>
  ),
});

export default ArtifactComponentWithSentry;
